/**
 * Fantasy Console 0 - Auth Context
 *
 * Provides authentication state management with Supabase.
 * Handles sign up, sign in, sign out, and session management.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase, isSupabaseConfigured } from '../lib/supabase'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  signUp: (email: string, password: string) => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  updateUsername: (newUsername: string) => Promise<void>
  username: string | null
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [username, setUsername] = useState<string | null>(null)

  // Fetch username from profiles table
  const fetchUsername = useCallback(async (userId: string) => {
    if (!supabase) return

    const { data, error } = await supabase
      .from('profiles')
      .select('username')
      .eq('id', userId)
      .single()

    if (error) {
      console.error('Failed to fetch username:', error)
      return
    }

    setUsername(data?.username || null)
  }, [])

  // Initialize auth state
  useEffect(() => {
    if (!supabase) {
      setLoading(false)
      return
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchUsername(session.user.id)
      }
      setLoading(false)
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchUsername(session.user.id)
      } else {
        setUsername(null)
      }
    })

    return () => subscription.unsubscribe()
  }, [fetchUsername])

  const signUp = useCallback(async (email: string, password: string) => {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    // Sign up the user
    const { error: signUpError } = await supabase.auth.signUp({
      email,
      password,
    })

    if (signUpError) {
      throw signUpError
    }

    // Note: Username can be set later via updateUsername
    // The profiles table trigger will auto-create the profile row
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      throw error
    }
  }, [])

  const signOut = useCallback(async () => {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    const { error } = await supabase.auth.signOut()

    if (error) {
      throw error
    }

    setUsername(null)
  }, [])

  const updateUsername = useCallback(
    async (newUsername: string) => {
      if (!supabase) {
        throw new Error('Supabase is not configured')
      }

      if (!user) {
        throw new Error('You must be signed in to change your username')
      }

      // Validate username format
      if (!isValidUsername(newUsername)) {
        throw new Error(
          'Username must be 3-30 characters and only contain letters, numbers, hyphens, and underscores',
        )
      }

      // Check if username is available (exclude current user)
      const { data: existingUser, error: checkError } = await supabase
        .from('profiles')
        .select('username')
        .eq('username', newUsername)
        .neq('id', user.id)
        .maybeSingle()

      if (checkError) {
        throw new Error('Failed to check username availability')
      }

      if (existingUser) {
        throw new Error('Username is already taken')
      }

      // Update username
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ username: newUsername })
        .eq('id', user.id)

      if (updateError) {
        throw new Error('Failed to update username')
      }

      setUsername(newUsername)
    },
    [user],
  )

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signUp,
        signIn,
        signOut,
        updateUsername,
        username,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * Validate username format:
 * - 3-30 characters
 * - Only letters, numbers, hyphens, underscores
 */
export function isValidUsername(username: string): boolean {
  if (username.length < 3 || username.length > 30) {
    return false
  }
  return /^[a-zA-Z0-9_-]+$/.test(username)
}
